import type { RcFile } from 'antd/es/upload';

/**
 * Initiates a file download by creating and clicking a temporary anchor element.
 * @param {{href: string, fileName: string}} args - The arguments object.
 * @param {string} args.href - The URL of the file to download.
 * @param {string} args.fileName - The desired name for the downloaded file.
 */
export const handleDownload = (args: { href: string; fileName: string }) => {
  const { href, fileName } = args;

  const link = document.createElement('a');
  link.href = href;
  link.download = fileName;
  link.click();
};

/**
 * Exports a JSON object as a downloadable file with the specified subject and file type.
 * @param {JSON|JSON[]} json - The JSON object to export.
 * @param {string} subject - The base name for the exported file.
 */
export const exportAsJSON = (json: JSON | JSON[], subject: string) => {
  handleDownload({
    href: `data:text/plain;charset=utf-8,${encodeURIComponent(JSON.stringify(json))}`,
    fileName: `${subject}.json`,
  });
};

export const exportAsCSV = <T extends Record<string, unknown>>(
  data: T[],
  filename: string,
): void => {
  if (!data?.length) return;

  /**
   * Convert a value to a safe CSV string.
   * - Objects/arrays become JSON strings.
   * - Strings with commas, quotes, or newlines are escaped with double quotes.
   * - Existing double quotes are doubled.
   */
  const escapeCSV = (value: unknown): string => {
    if (value === null || value === undefined) return '';

    let stringValue: string;
    if (typeof value === 'object') {
      stringValue = JSON.stringify(value);
    } else {
      stringValue = String(value);
    }

    // If the value contains comma, newline, or double quote, enclose in double quotes
    if (
      stringValue.includes(',') ||
      stringValue.includes('\n') ||
      stringValue.includes('"')
    ) {
      // Replace double quotes with two double quotes
      stringValue = stringValue.replace(/"/g, '""');
      return `"${stringValue}"`;
    }
    return stringValue;
  };

  // Get all unique headers from all objects
  const headers = Array.from(new Set(data.flatMap((obj) => Object.keys(obj))));

  // Build CSV rows
  const csvRows = [
    headers.join(','), // header row
    ...data.map((row) =>
      headers.map((header) => escapeCSV(row[header])).join(','),
    ),
  ];

  const csvContent = csvRows.join('\n');
  // Add BOM for Excel UTF-8 compatibility
  const blob = new Blob(['\uFEFF' + csvContent], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const isPdf = (file: RcFile | File) => file.type === 'application/pdf';
export const isImage = (file: RcFile | File) =>
  ['image/png', 'image/jpeg'].includes(file.type);
export const isAllowedMb = (file: RcFile | File, mb: number) =>
  file.size / 1024 / 1024 < mb;

export const SVGToBase64 = (svg: string) =>
  btoa(
    encodeURIComponent(svg).replace(/%([0-9A-F]{2})/g, (_, p1) =>
      String.fromCharCode(parseInt(p1, 16)),
    ),
  );
